import React, { useState, useEffect, useRef } from "react";
import { Form, Select, DatePicker, Button } from "antd";
import Chart from "chart.js/auto";
import { useDispatch, useSelector } from "react-redux";
import { getStates } from "../features/statecity/statecitySlice";
import { getUsers } from "../features/cutomers/customerSlice";
import { getIndustrialvisit } from "../features/industrial-visit/industrialSlice";
import { getGuestLectures } from "../features/guest-lectures/guestlectureSlice";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const GuestLectures = () => {
  const [activeTab, setActiveTab] = useState("guestLectures");
  const [vcData, setvcData] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedVC, setselectedVC] = useState("");
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [chartInstance, setChartInstance] = useState(null);
  const [processedData, setProcessedData] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const statelistState = useSelector(
    (state) => state.statecity.statelist || []
  );
  const vclistState = useSelector((state) => state.customer.customers || []);
  const industrialVisitData = useSelector(
    (state) => state.industrialvisit.industrialvisits || {}
  );
  const guestLectureData = useSelector(
    (state) => state.guestlectures.guestlecture || {}
  );

  useEffect(() => {
    dispatch(getStates());
    dispatch(getUsers());
    dispatch(getIndustrialvisit());
    dispatch(getGuestLectures());
  }, [dispatch]);

  const chartInstanceRef = useRef(null);
  const pdfExportRef = useRef(null);

  const processData = (data, type, filters = {}) => {
    if (!data?.length)
      return { tableData: [], chartData: { labels: [], datasets: [] } };

    const { state_name, vc_name, start_date, end_date } = filters;

    const filteredData = data.filter((item) => {
      const stateMatch = !state_name || item.createdBy?.state === state_name;
      const vcMatch = !vc_name || item.createdBy?.vcUser?._id === vc_name;
      const dateMatch =
        !start_date ||
        !end_date ||
        (new Date(item.createdAt) >= new Date(start_date) &&
          new Date(item.createdAt) <= new Date(end_date));
      return stateMatch && vcMatch && dateMatch;
    });

    const summaryTable = {};
    const summaryChart = {};

    filteredData.forEach((item) => {
      const state = item?.createdBy?.state || "Unknown State";
      const vcName = item?.createdBy?.vcUser?.name || "Unknown VC";
      const key = `${state} / ${vcName}`;

      if (!summaryTable[key]) {
        summaryTable[key] = { planned: 0, completed: 0 };
      }
      summaryTable[key].planned += 1;
      if (item.approve) {
        summaryTable[key].completed += 1;
      }

      if (!summaryChart[state]) {
        summaryChart[state] = { planned: 0, completed: 0 };
      }
      summaryChart[state].planned += 1;
      if (item.approve) {
        summaryChart[state].completed += 1;
      }
    });

    const tableData = Object.keys(summaryTable).map((key) => ({
      state_vc: key,
      planned: summaryTable[key].planned,
      completed: summaryTable[key].completed,
      percentage: (
        (summaryTable[key].completed / summaryTable[key].planned) *
        100
      ).toFixed(2),
    }));

    const chartData = {
      labels: Object.keys(summaryChart),
      datasets: [
        {
          label: `${type} Planned`,
          data: Object.values(summaryChart).map((item) => item.planned),
          backgroundColor: "#f44336",
        },
        {
          label: `${type} Completed`,
          data: Object.values(summaryChart).map((item) => item.completed),
          backgroundColor: "#4caf50",
        },
      ],
    };

    return { tableData, chartData };
  };

  useEffect(() => {
    let selectedData;
    if (activeTab === "guestLectures") {
      selectedData = processData(guestLectureData.data || [], "Lectures");
    } else {
      selectedData = processData(industrialVisitData.data || [], "Visits");
    }
    setProcessedData(selectedData.tableData);
    setChartData(selectedData.chartData);
  }, [activeTab, guestLectureData, industrialVisitData]);

  const handleStateChange = (value) => {
    const vcfilterData = vclistState.filter(
      (customer) => customer.state === value
    );
    setvcData(vcfilterData);
    setSelectedState(value);
  };

  const handleFilterSubmit = (values) => {
    const filters = {
      state_name: values.state_name,
      vc_name: values.vc_name,
      start_date: values.start_date
        ? values.start_date.format("YYYY-MM-DD")
        : null,
      end_date: values.end_date ? values.end_date.format("YYYY-MM-DD") : null,
    };

    let selectedData;
    if (activeTab === "guestLectures") {
      selectedData = processData(
        guestLectureData.data || [],
        "Lectures",
        filters
      );
    } else {
      selectedData = processData(
        industrialVisitData.data || [],
        "Visits",
        filters
      );
    }
    setProcessedData(selectedData.tableData);
    setChartData(selectedData.chartData);
  };

  const handleClear = () => {
    form.resetFields();
    setSelectedState("");
    setselectedVC("");
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    setvcData([]);
    let selectedData;
    if (activeTab === "guestLectures") {
      selectedData = processData(guestLectureData.data || [], "Lectures");
    } else {
      selectedData = processData(industrialVisitData.data || [], "Visits");
    }
    setProcessedData(selectedData.tableData);
    setChartData(selectedData.chartData);
  };

  const renderChart = (data) => {
    if (chartInstance) {
      chartInstance.destroy();
    }

    const ctx = chartInstanceRef.current.getContext("2d");
    const newChartInstance = new Chart(ctx, {
      type: "bar",
      data,
      options: {
        responsive: true,
        plugins: {
          legend: { position: "top" },
          tooltip: {
            callbacks: {
              label: function (tooltipItem) {
                const total = data.datasets.reduce(
                  (acc, dataset) => acc + dataset.data[tooltipItem.dataIndex],
                  0
                );
                const percentage = ((tooltipItem.raw / total) * 100).toFixed(2);
                return `${tooltipItem.dataset.label}: ${tooltipItem.raw} (${percentage}%)`;
              },
            },
          },
        },
        scales: {
          x: { title: { display: true, text: "States" } },
          y: {
            title: { display: true, text: "Number of Events" },
            ticks: { stepSize: 1 },
          },
        },
      },
    });

    setChartInstance(newChartInstance);
  };

  useEffect(() => {
    if (chartData && chartInstanceRef.current) {
      renderChart(chartData);
    }
  }, [chartData]);

  const exportAsPDF = () => {
    const input = pdfExportRef.current;
    html2canvas(input).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save("guest_lectures_industrial_visits_report.pdf");
    });
  };

  const styles = {
    table: {
      width: "100%",
      borderCollapse: "collapse",
      marginTop: "10px",
    },
    tableHeader: {
      backgroundColor: "#e5e7eb",
      fontWeight: "bold",
    },
    tableCell: {
      padding: "10px",
      textAlign: "center",
    },
    button: {
      padding: "10px 15px",
      borderRadius: "4px",
      fontWeight: "bold",
      cursor: "pointer",
      border: "none",
      marginRight: "10px",
    },
    activeButton: {
      backgroundColor: "#3b82f6",
      color: "white",
      marginTop: "20px",
    },
    inactiveButton: {
      backgroundColor: "#e5e7eb",
      color: "#374151",
      marginTop: "20px",
    },
  };

  return (
    <div
      style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}
      ref={pdfExportRef}
    >
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <h4
          style={{
            fontSize: "large",
            fontWeight: "bold",
            margin: "-20px 0 20px -20px",
          }}
        >
          Guest Lectures & Industrial Visits Overview
        </h4>
        <p
          style={{
            textAlign: "right",
            fontSize: "0.875rem",
            margin: "-20px 20px 0 0",
            color: "rgb(107, 114, 128)",
          }}
        >
          Last Updated: 10:30 AM
        </p>
      </div>

      <Form
        form={form}
        layout="inline"
        onFinish={handleFilterSubmit}
        style={{ marginTop: "20px", marginLeft: "50px" }}
      >
        <Form.Item
          name="state_name"
          rules={[{ required: true, message: "Please Select State!" }]}
        >
          <Select
            style={{ width: 160, marginRight: 16 }}
            placeholder="Select State"
            onChange={handleStateChange}
          >
            {statelistState.map((i, j) => (
              <Select.Option key={j} value={i.state}>
                {i.state_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="vc_name"
          rules={[{ required: true, message: "Please Select VC!" }]}
        >
          <Select
            style={{ width: 160, marginRight: 16 }}
            placeholder="Select VC"
            onChange={(value) => setselectedVC(value)}
          >
            {vcData
              .filter((item) => item.role === "vc")
              .map((item) => (
                <Select.Option key={item._id} value={item._id}>
                  {item.name}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="start_date"
          rules={[{ required: true, message: "Select date!" }]}
        >
          <DatePicker
            placeholder="From"
            style={{ width: 100 }}
            onChange={(value) => {
              setSelectedStartDate(value ? value.format("YYYY-MM-DD") : null);
            }}
          />
        </Form.Item>
        <Form.Item
          name="end_date"
          rules={[{ required: true, message: "Select date!" }]}
        >
          <DatePicker
            placeholder="To"
            style={{ width: 100 }}
            onChange={(value) => {
              setSelectedEndDate(value ? value.format("YYYY-MM-DD") : null);
            }}
          />
        </Form.Item>
        <Form.Item>
          <Button
            style={{
              backgroundColor: "#28a745",
              color: "#fff",
              marginTop: 16,
              marginLeft: 385,
            }}
            htmlType="submit"
          >
            Apply Filter
          </Button>
        </Form.Item>
        <Form.Item>
          <Button
            style={{
              backgroundColor: "#dc3545",
              color: "#fff",
              marginTop: 16,
              marginLeft: -12,
            }}
            onClick={handleClear}
          >
            Reset Filter
          </Button>
        </Form.Item>
      </Form>

      <div
        style={{
          backgroundColor: "#DCDCDC",
          boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
          borderRadius: "0.5rem",
          marginTop: "20px",
          padding: "10px",
          width: "auto",
          height: "auto",
        }}
      >
        <canvas ref={chartInstanceRef}></canvas>
      </div>

      <div>
        <Button
          style={
            activeTab === "guestLectures"
              ? styles.activeButton
              : styles.inactiveButton
          }
          onClick={() => setActiveTab("guestLectures")}
        >
          Guest Lectures
        </Button>
        <Button
          style={
            activeTab === "industryVisits"
              ? styles.activeButton
              : styles.inactiveButton
          }
          onClick={() => setActiveTab("industryVisits")}
        >
          Industry Visits
        </Button>
      </div>

      {activeTab === "guestLectures" && (
        <div
          className="table-container"
          style={{
            backgroundColor: "#ffffff",
            borderRadius: "0.5rem",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            padding: "1rem",
            marginTop: "0.5rem",
          }}
        >
          <table style={styles.table}>
            <thead style={styles.tableHeader}>
              <tr>
                <th style={styles.tableCell}>State / VC Name</th>
                <th style={styles.tableCell}>Lecture Planned</th>
                <th style={styles.tableCell}>Lecture Done</th>
                <th style={styles.tableCell}>%</th>
              </tr>
            </thead>
            <tbody>
              {processedData.length > 0 ? (
                processedData.map((item, index) => (
                  <tr key={index}>
                    <td style={styles.tableCell}>{item.state_vc}</td>
                    <td style={styles.tableCell}>{item.planned}</td>
                    <td style={styles.tableCell}>{item.completed}</td>
                    <td style={styles.tableCell}>{item.percentage}%</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="4"
                    style={{ textAlign: "center", padding: "10px" }}
                  >
                    No Data Available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === "industryVisits" && (
        <div
          className="table-container"
          style={{
            backgroundColor: "#ffffff",
            borderRadius: "0.5rem",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            padding: "1rem",
            marginTop: "0.5rem",
          }}
        >
          <table style={styles.table}>
            <thead style={styles.tableHeader}>
              <tr>
                <th style={styles.tableCell}>State / VC Name</th>
                <th style={styles.tableCell}>Visit Planned</th>
                <th style={styles.tableCell}>Visit Done</th>
                <th style={styles.tableCell}>%</th>
              </tr>
            </thead>
            <tbody>
              {processedData.length > 0 ? (
                processedData.map((item, index) => (
                  <tr key={index}>
                    <td style={styles.tableCell}>{item.state_vc}</td>
                    <td style={styles.tableCell}>{item.planned}</td>
                    <td style={styles.tableCell}>{item.completed}</td>
                    <td style={styles.tableCell}>{item.percentage}%</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="4"
                    style={{ textAlign: "center", padding: "10px" }}
                  >
                    No Data Available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: "20px", display: "flex", gap: "15px" }}>
        <Button
          style={{
            padding: "8px 12px",
            color: "#fff",
            border: "none",
            cursor: "pointer",
            backgroundColor: "#3B82F6",
            borderRadius: "5px",
          }}
          onClick={exportAsPDF}
        >
          Export as PDF
        </Button>
        <Button
          style={{
            padding: "8px 12px",
            color: "#fff",
            border: "none",
            cursor: "pointer",
            backgroundColor: "#3B82F6",
            borderRadius: "5px",
          }}
        >
          Export as CSV
        </Button>
      </div>
    </div>
  );
};

export default GuestLectures;
